import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, Modal, Select, Text } from '@viapeople/components'
import React, { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useToast } from '../../../../utils/ToastContext'
import { trpc } from '../../../../utils/trpc'
import type { User } from '../../../users/types'
import { AddReviewersModalWrapper } from './AddReviewersModalWrapper'

interface CycleReviewersAddModalProps {
	isOpen: boolean
	onClose: () => void
}

export const CycleReviewersAddModal: React.FC<CycleReviewersAddModalProps> = ({ isOpen, onClose }) => {
	const { cycleId, organizationId } = useParams<{ cycleId: string; organizationId: string }>()
	const { showToast } = useToast()
	const queryClient = useQueryClient()

	const [selectedParticipantId, setSelectedParticipantId] = useState<string>('')
	const [selectedGroupId, setSelectedGroupId] = useState<string>('')
	const [selectedReviewers, setSelectedReviewers] = useState<User[]>([])
	const [selectedStepId, setSelectedStepId] = useState<string>('')
	const [isAddReviewersModalOpen, setIsAddReviewersModalOpen] = useState(false)

	// Debug effect to track selection changes
	React.useEffect(() => {
		console.log(
			'[CycleReviewersAddModal] selectedReviewers state updated:',
			selectedReviewers.length,
			selectedReviewers,
		)
	}, [selectedReviewers])

	// Fetch reviewer groups
	const { data: groups } = useQuery({
		...trpc.admin.cycles.reviewerGroups.search.queryOptions({
			cycleId: cycleId as string,
			search: '',
			perPage: 99999,
		}),
		enabled: !!cycleId && isOpen,
	})

	// Fetch participants
	const { data: participants } = useQuery({
		...trpc.admin.cycles.participants.search.queryOptions({
			cycleId: cycleId as string,
			search: '',
			perPage: 99999,
		}),
		enabled: !!cycleId && !!organizationId && isOpen,
	})

	// Fetch cycle to get organizationId
	const { data: cycle } = useQuery({
		...trpc.admin.cycles.getById.queryOptions({ id: cycleId as string }),
		enabled: !!cycleId && isOpen,
	})

	// Fetch GROUP_EVALUATION steps
	const { data: groupEvaluationSteps } = useQuery({
		...trpc.admin.cycles.steps.groupEvaluationSteps.queryOptions({ cycleId: cycleId as string }),
		enabled: !!cycleId && isOpen,
	})

	// Add reviewers mutation
	const addReviewersMutation = useMutation({
		...trpc.admin.cycles.reviewers.addPostLaunch.mutationOptions({
			onSuccess: (data) => {
				console.log('[CycleReviewersAddModal] Success:', data)
				showToast(`Successfully added ${data.reviewersAdded} rater(s)! Created ${data.tasksCreated} task(s).`, 'info')

				if (data.warnings.length > 0) {
					data.warnings.forEach((warning) => {
						showToast(warning, 'info')
					})
				}

				// Invalidate relevant queries
				queryClient.invalidateQueries({
					queryKey: ['admin', 'cycles', 'reviewers', 'search'],
				})

				// Reset form and close
				handleReset()
				onClose()
			},
			onError: (error) => {
				console.error('[CycleReviewersAddModal] Error:', error)
				showToast(`Failed to add raters: ${error.message || 'Unknown error'}`, 'error')
			},
		}),
	})

	const handleReset = () => {
		setSelectedParticipantId('')
		setSelectedReviewers([])
		setSelectedGroupId('')
		setSelectedStepId('')
	}

	const handleSubmit = () => {
		console.log('[CycleReviewersAddModal] handleSubmit called', {
			selectedParticipantId,
			selectedReviewersCount: selectedReviewers.length,
			selectedGroupId,
		})

		if (!selectedParticipantId || selectedReviewers.length === 0 || !selectedGroupId) {
			showToast('Please select a participant, at least one rater, and a rater group', 'error')
			return
		}

		const participant = participants?.data.find((p) => p.userId === selectedParticipantId)
		if (!participant) {
			showToast('Participant not found', 'error')
			return
		}

		const mutationInput = {
			cycleId: cycleId as string,
			participantExternalId: participant.externalId,
			reviewers: selectedReviewers.map((reviewer) => ({
				reviewerExternalId: reviewer.externalId,
				reviewerGroupId: selectedGroupId,
			})),
			...(selectedStepId && { stepId: selectedStepId }),
		}

		console.log('[CycleReviewersAddModal] Calling mutation with:', mutationInput)
		addReviewersMutation.mutate(mutationInput)
	}

	const handleOpenReviewerSelection = () => {
		if (!selectedParticipantId) {
			showToast('Please select a participant first', 'error')
			return
		}
		if (!selectedGroupId) {
			showToast('Please select a rater group first', 'error')
			return
		}
		setIsAddReviewersModalOpen(true)
	}

	const handleReviewersSelected = (users: User[]) => {
		console.log('[CycleReviewersAddModal] Raters selected:', users.length, users)
		setSelectedReviewers(users)
		setIsAddReviewersModalOpen(false)
	}

	// Get excluded user IDs (existing raters for this participant)
	const getExcludedUserIds = (): Set<string> => {
		// In a real scenario, you'd fetch existing raters for this participant
		// For now, return empty set
		return new Set()
	}

	const participantOptions =
		participants?.data.map((p) => ({
			value: p.userId,
			label: `${p.firstName} ${p.lastName} (${p.externalId})`,
		})) || []

	const groupOptions =
		groups?.data.map((g) => ({
			value: g.id,
			label: g.name,
		})) || []

	const stepOptions =
		groupEvaluationSteps?.map((s) => ({
			value: s.id,
			label: s.name,
		})) || []

	return (
		<>
			<Modal
				isOpen={isOpen}
				onClose={onClose}
				title='Add Raters to Participant'
				buttons={[
					{
						children: 'Cancel',
						variant: 'secondary',
						onClick: onClose,
					},
					{
						children: addReviewersMutation.isPending ? 'Adding...' : 'Add Raters',
						variant: 'primary',
						onClick: handleSubmit,
						disabled:
							!selectedParticipantId ||
							selectedReviewers.length === 0 ||
							!selectedGroupId ||
							addReviewersMutation.isPending,
					},
				]}
				buttonsAlignment='right'>
				<div className='space-y-4'>
					<div>
						<Text variant='medium' className='mb-2 font-medium'>
							Select Participant
						</Text>
						<Select
							options={participantOptions}
							value={selectedParticipantId}
							onChange={(value) => setSelectedParticipantId(value as string)}
							placeholder='Choose a participant...'
						/>
					</div>

					<div>
						<Text variant='medium' className='mb-2 font-medium'>
							Select Rater Group
						</Text>
						<Select
							options={groupOptions}
							value={selectedGroupId}
							onChange={(value) => setSelectedGroupId(value as string)}
							placeholder='Choose a rater group...'
						/>
					</div>

					{stepOptions.length > 0 && (
						<div>
							<Text variant='medium' className='mb-2 font-medium'>
								Select Step (Optional)
							</Text>
							<Select
								options={[{ value: '', label: 'Current step (default)' }, ...stepOptions]}
								value={selectedStepId}
								onChange={(value) => setSelectedStepId(value as string)}
								placeholder='Choose a step...'
							/>
							<Text variant='small' className='mt-1 text-gray-600'>
								Only specify a step if you need to add raters to a specific GROUP_EVALUATION step that is not currently
								active.
							</Text>
						</div>
					)}

					<div>
						<Text variant='medium' className='mb-2 font-medium'>
							Select Raters ({selectedReviewers.length} selected)
						</Text>
						<Button onClick={handleOpenReviewerSelection} className='w-full' variant='secondary'>
							{selectedReviewers.length === 0 ? 'Choose Raters...' : 'Change Raters...'}
						</Button>
						{selectedReviewers.length > 0 && (
							<div className='mt-2 p-3 bg-green-50 rounded border-2 border-green-300'>
								<Text variant='medium' className='text-green-800 font-semibold mb-2'>
									✓ {selectedReviewers.length} Rater{selectedReviewers.length !== 1 ? 's' : ''} Selected
								</Text>
								<div className='text-sm text-green-700 mb-3 max-h-32 overflow-y-auto'>
									{selectedReviewers.map((r, idx) => (
										<div key={r.id || idx}>
											• {r.firstName} {r.lastName} ({r.externalId})
										</div>
									))}
								</div>
								<Button
									onClick={handleSubmit}
									disabled={
										!selectedParticipantId ||
										selectedReviewers.length === 0 ||
										!selectedGroupId ||
										addReviewersMutation.isPending
									}
									className='w-full'>
									{addReviewersMutation.isPending
										? 'Adding Raters...'
										: `Submit - Add ${selectedReviewers.length} Rater${selectedReviewers.length !== 1 ? 's' : ''} to Participant`}
								</Button>
							</div>
						)}
					</div>

					<div className='bg-blue-50 border border-blue-200 rounded p-3 mt-4'>
						<div className='text-sm text-blue-700'>
							<strong>Note:</strong> This will create tasks for the current GROUP_EVALUATION step (if active) and send
							kick-off emails immediately. Duplicate raters will be skipped.
						</div>
					</div>
				</div>
			</Modal>

			<AddReviewersModalWrapper
				isOpen={isAddReviewersModalOpen}
				onClose={() => {
					console.log('[CycleReviewersAddModal] Closing rater selection modal')
					setIsAddReviewersModalOpen(false)
				}}
				organizationId={cycle?.organizationId || organizationId}
				excludedUserExternalIds={getExcludedUserIds}
				onSelect={handleReviewersSelected}
			/>
		</>
	)
}
